import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
    PiArrowDownBold,
    PiArrowUpBold,
    PiArrowsClockwiseBold,
    PiCalendarBlank,
    PiCheckCircleBold,
    PiCurrencyDollarBold,
    PiGearBold,
    PiWarningBold,
} from 'react-icons/pi';
import { Button, PageHeader, LoadingState } from '@v2/design-system';
import api from '@v2/lib/api';

type MonthRow = {
    month: string;
    installments_count: number;
    deals_count: number;
    total_amount: number;
    paid_amount: number;
    commission_total: number;
    commission_paid: number;
    commission_pending: number;
    commission_cascade_paid: number;
    projected_received: number;
    projected_pending: number;
    sale_total: number;
    sale_paid: number;
    sale_pending: number;
    is_past: boolean;
    is_current: boolean;
    is_future: boolean;
};

type Meta = {
    total_pipeline: number;
    projected_received: number;
    projected_pending: number;
    actually_paid: number;
    past_overdue: number;
    sale_pipeline?: number;
    months_count: number;
    current_month: string;
    metric?: string;
};

type CashflowResponse = {
    data: MonthRow[];
    meta: Meta;
};

const fmt = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v);

const MONTH_NAMES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

function fmtYearMonth(ym: string) {
    const [y, m] = ym.split('-');
    return `${MONTH_NAMES[parseInt(m, 10) - 1]}/${y}`;
}

function SummaryCard({
    label,
    value,
    sub,
    icon,
    accent,
}: {
    label: string;
    value: string;
    sub?: string;
    icon: React.ReactNode;
    accent: string;
}) {
    return (
        <div className={`rounded-xl border px-4 py-3 ${accent}`}>
            <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {icon}
                {label}
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-zinc-100">{value}</p>
            {sub && <p className="mt-0.5 text-xs text-gray-500">{sub}</p>}
        </div>
    );
}

const BAR_MAX_H = 120;

function BarChart({ rows, maxAmount }: { rows: MonthRow[]; maxAmount: number }) {
    return (
        <div className="overflow-x-auto pb-2">
            <div className="flex min-w-max items-end gap-1 px-1" style={{ height: BAR_MAX_H + 32 }}>
                {rows.map((r) => {
                    const amount = r.commission_total ?? r.total_amount;
                    const h = maxAmount > 0 ? Math.max(4, (amount / maxAmount) * BAR_MAX_H) : 4;
                    const color = r.is_past
                        ? 'bg-emerald-400'
                        : r.is_current
                          ? 'bg-amber-400'
                          : 'bg-sky-400';
                    return (
                        <div key={r.month} className="group flex flex-col items-center gap-0.5">
                            <div className="relative">
                                <div className="pointer-events-none absolute bottom-full left-1/2 mb-1 hidden -translate-x-1/2 rounded bg-gray-800 px-2 py-1 text-center text-[10px] text-white group-hover:block">
                                    <span className="font-semibold">{fmtYearMonth(r.month)}</span>
                                    <br />
                                    Comissão: {fmt(amount)}
                                    <br />
                                    Venda: {fmt(r.sale_total ?? 0)}
                                    <br />
                                    {r.installments_count} parcela(s)
                                </div>
                            </div>
                            <div
                                className={`w-7 rounded-t transition-all ${color}`}
                                style={{ height: h }}
                            />
                            <span className="w-7 -rotate-45 text-[9px] text-gray-500 dark:text-zinc-500">
                                {fmtYearMonth(r.month).split('/')[0]}
                            </span>
                        </div>
                    );
                })}
            </div>
            <div className="mt-1 flex items-center gap-4 px-1 text-[10px] text-gray-500">
                <span className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded bg-emerald-400" /> Passado</span>
                <span className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded bg-amber-400" /> Mês atual</span>
                <span className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded bg-sky-400" /> Futuro</span>
            </div>
        </div>
    );
}

export default function CommissionCashflowPage() {
    const qc = useQueryClient();

    const { data, isLoading } = useQuery<CashflowResponse>({
        queryKey: ['commissioning', 'cashflow'],
        queryFn: () => api.get('/commissioning/installments/cashflow').then((r) => r.data),
        staleTime: 5 * 60 * 1000,
    });

    const bulkGenerate = useMutation({
        mutationFn: () =>
            api.post('/commissioning/installments/bulk-generate').then((r) => r.data),
        onSuccess: (data) => {
            const msg = data?.message ?? 'Parcelas geradas com sucesso.';
            toast.success(msg);
            qc.invalidateQueries({ queryKey: ['commissioning', 'cashflow'] });
        },
        onError: () => toast.error('Erro ao gerar parcelas.'),
    });

    const rebalance = useMutation({
        mutationFn: () =>
            api.post('/commissioning/installments/rebalance').then((r) => r.data),
        onSuccess: (data) => {
            const msg = data?.message ?? 'Re-sincronização iniciada.';
            toast.success(msg, { duration: 6000 });
        },
        onError: () => toast.error('Erro ao iniciar re-sincronização.'),
    });

    const rows = data?.data ?? [];
    const meta = data?.meta;
    const maxAmount = Math.max(...rows.map((r) => r.commission_total ?? r.total_amount), 1);

    return (
        <div className="space-y-6">
            <PageHeader
                title="Fluxo de Comissão"
                breadcrumb={[
                    { name: 'Comissionamento', href: '/commissioning' },
                    { name: 'Fluxo de Comissão' },
                ]}
            >
                <Button
                    size="sm"
                    onClick={() => bulkGenerate.mutate()}
                    isLoading={bulkGenerate.isPending}
                    className="bg-violet-600 text-white hover:bg-violet-700"
                >
                    <PiGearBold className="me-1.5 h-4 w-4" />
                    Gerar parcelas de todos os deals
                </Button>
                <Button
                    size="sm"
                    onClick={() => rebalance.mutate()}
                    isLoading={rebalance.isPending}
                    className="bg-amber-600 text-white hover:bg-amber-700"
                    title="Busca o número real de parcelas no Ploomes e re-gera onde a quantidade diverge"
                >
                    <PiArrowsClockwiseBold className="me-1.5 h-4 w-4" />
                    Re-sincronizar com Ploomes
                </Button>
            </PageHeader>

            {isLoading ? (
                <LoadingState variant="dashboard" />
            ) : (
                <>
                    <p className="text-sm text-gray-500 dark:text-zinc-400">
                        Comissão do vendedor rateada por vencimento; cascata gerente/diretor só entra quando a parcela está
                        marcada paga. &quot;Projetada&quot; = due_date ≤ hoje (não é NF paga — isso fica em Registros / Visão Geral).
                        Venda do cliente é só referência.
                    </p>

                    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                        <SummaryCard
                            label="Pipeline de comissão"
                            value={fmt(meta?.total_pipeline ?? 0)}
                            sub={`Venda ref.: ${fmt(meta?.sale_pipeline ?? 0)} · ${meta?.months_count ?? 0} meses`}
                            icon={<PiCurrencyDollarBold className="h-3.5 w-3.5 text-gray-400" />}
                            accent="border-gray-200 bg-gray-50 dark:border-zinc-700 dark:bg-zinc-800"
                        />
                        <SummaryCard
                            label="Comissão projetada (recebida)"
                            value={fmt(meta?.projected_received ?? 0)}
                            sub="Corte due_date ≤ hoje (≠ NF paga)"
                            icon={<PiCheckCircleBold className="h-3.5 w-3.5 text-emerald-400" />}
                            accent="border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/20"
                        />
                        <SummaryCard
                            label="Comissão a receber"
                            value={fmt(meta?.projected_pending ?? 0)}
                            sub="Parcelas futuras (projeção)"
                            icon={<PiArrowUpBold className="h-3.5 w-3.5 text-sky-400" />}
                            accent="border-sky-200 bg-sky-50 dark:border-sky-800 dark:bg-sky-950/20"
                        />
                        <SummaryCard
                            label="Vencido em aberto"
                            value={fmt(meta?.past_overdue ?? 0)}
                            sub="Parcela não quitada em meses passados"
                            icon={<PiWarningBold className="h-3.5 w-3.5 text-rose-400" />}
                            accent={
                                (meta?.past_overdue ?? 0) > 0
                                    ? 'border-rose-200 bg-rose-50 dark:border-rose-800 dark:bg-rose-950/20'
                                    : 'border-gray-200 bg-gray-50 dark:border-zinc-700 dark:bg-zinc-800'
                            }
                        />
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
                        <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-zinc-300">
                            <PiCalendarBlank className="h-4 w-4" />
                            Comissão por mês
                        </h3>
                        <BarChart rows={rows} maxAmount={maxAmount} />
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:bg-zinc-800">
                                    <tr>
                                        <th className="px-4 py-2.5">Mês</th>
                                        <th className="px-4 py-2.5 text-right">Parcelas</th>
                                        <th className="px-4 py-2.5 text-right">Deals</th>
                                        <th className="px-4 py-2.5 text-right">Comissão</th>
                                        <th className="px-4 py-2.5 text-right">Venda (ref.)</th>
                                        <th className="px-4 py-2.5 text-right">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                                    {rows.map((r) => (
                                        <tr
                                            key={r.month}
                                            className={
                                                r.is_current
                                                    ? 'bg-amber-50/60 font-semibold dark:bg-amber-950/10'
                                                    : r.is_past
                                                      ? 'bg-emerald-50/30 dark:bg-emerald-950/10'
                                                      : ''
                                            }
                                        >
                                            <td className="px-4 py-2">
                                                <span className="inline-flex items-center gap-2">
                                                    {fmtYearMonth(r.month)}
                                                    {r.is_current && (
                                                        <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
                                                            Atual
                                                        </span>
                                                    )}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2 text-right text-gray-600 dark:text-zinc-400">
                                                {r.installments_count}
                                            </td>
                                            <td className="px-4 py-2 text-right text-gray-600 dark:text-zinc-400">
                                                {r.deals_count}
                                            </td>
                                            <td className="px-4 py-2 text-right font-semibold text-gray-900 dark:text-zinc-100">
                                                {fmt(r.commission_total ?? r.total_amount)}
                                                {(r.commission_cascade_paid ?? 0) > 0 && (
                                                    <span className="mt-0.5 block text-[10px] font-normal text-violet-600">
                                                        cascata paga {fmt(r.commission_cascade_paid)}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 text-right text-xs text-gray-500">
                                                {fmt(r.sale_total ?? 0)}
                                            </td>
                                            <td className="px-4 py-2 text-right">
                                                {r.is_past || r.is_current ? (
                                                    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                                                        <PiArrowDownBold className="h-3 w-3" />
                                                        Projetado
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-xs font-medium text-sky-600">
                                                        <PiArrowUpBold className="h-3 w-3" />
                                                        A receber
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                {meta && (
                                    <tfoot className="border-t-2 border-gray-200 bg-gray-50 dark:border-zinc-700 dark:bg-zinc-800">
                                        <tr>
                                            <td className="px-4 py-2.5 text-xs font-semibold uppercase text-gray-500">
                                                Total
                                            </td>
                                            <td className="px-4 py-2.5 text-right text-xs font-semibold text-gray-700 dark:text-zinc-300">
                                                {rows.reduce((s, r) => s + r.installments_count, 0)}
                                            </td>
                                            <td className="px-4 py-2.5" />
                                            <td className="px-4 py-2.5 text-right font-bold text-gray-900 dark:text-zinc-100">
                                                {fmt(meta.total_pipeline)}
                                            </td>
                                            <td className="px-4 py-2.5 text-right text-xs text-gray-500">
                                                {fmt(meta.sale_pipeline ?? 0)}
                                            </td>
                                            <td className="px-4 py-2.5 text-right text-xs text-gray-500">
                                                ↑ {fmt(meta.projected_pending)} futuro
                                            </td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
